const DB_NAME = 'scraper_cache';
const DB_VERSION = 1;
const STORE_NAME = 'compressed_data';

// Open IndexedDB database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

function getStorageKey(dbKey: string): string {
  return `db_cache_${dbKey}`;
}

// Get data from IndexedDB
export async function getFromIndexedDB(key: string): Promise<ArrayBuffer | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(getStorageKey(key));

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as ArrayBuffer | undefined);
  });
}

export async function hasInIndexedDB(key: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.getAllKeys();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result.includes(getStorageKey(key)));
  });
}

// Set data in IndexedDB
export async function setInIndexedDB(key: string, data: ArrayBuffer): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(data, getStorageKey(key));

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Delete data from IndexedDB
export async function deleteFromIndexedDB(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(getStorageKey(key));

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function saveCurrentDbKey(dbKey: string): Promise<void> {
  localStorage.setItem('current_db_key', dbKey);
}

export async function getCurrentDbKey(): Promise<string | undefined> {
  let cachedKey = localStorage.getItem('current_db_key') ?? undefined;
  if (cachedKey) {
    const hasCachedKey = await hasInIndexedDB(cachedKey);
    if (!hasCachedKey) {
      cachedKey = undefined;
    }
  }

  return cachedKey;
}
